import { AlertColor, Box, Chip, Tooltip, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel, FormModel } from '../../../../components/Form';
import PageLoader from '../../../../components/PageLoader';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { User } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { Validators } from '../../../../utils/validators';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { EditOrgMasterDataFormModel } from './EditOrgMasterData/EditOrgMasterDataForm';

type Props = {
  isParent?: boolean;
};

const OrgMasterDataList: FC<Props> = ({ isParent = false }) => {
  const { id } = useParams() as { id?: string };
  const user = useSelector(User) as any;
  const isBbrAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';

  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);

  const [pageResponse, setPageResponse] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [dataType, setDataType] = useState('String');
  const [inputType, setInputType] = useState('text');
  const [isCustomKey, setIsCustomKey] = useState(false);
  const [masterDataKeyOptions, setMasterDataKeyOptions] = useState<{ label: string; value: string }[]>([]);
  const [dataTypeOptions, setDataTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message:
        error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message ?? 'Something went wrong',
    });
  };

  const getInputType = (type: string) => {
    switch (String(type || '').toLowerCase()) {
      case 'boolean':
        return 'checkbox';
      default:
        return 'text';
    }
  };

  const fetchDropdowns = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axiosInstance.get(URLS.orgMasterDataDropdowns(id, isParent));
      setMasterDataKeyOptions((data?.masterDataKeys ?? []).map((item: any) => ({ value: item.id, label: item.name })));
      setDataTypeOptions((data?.dataTypes ?? []).map((item: any) => ({ value: item.id, label: item.name })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, [id, isParent]);

  const renderValue = (item: any) => {
    const { masterDataValue, dataType: rowType } = item;

    if (!masterDataValue) return <Typography>-</Typography>;

    switch (String(rowType || '').toLowerCase()) {
      case 'boolean':
        return (
          <Chip
            label={masterDataValue === 'true' ? 'Yes' : 'No'}
            color={masterDataValue === 'true' ? 'success' : 'default'}
            size="small"
          />
        );
      case 'number':
        return <Typography variant="body2">{Number(masterDataValue).toLocaleString()}</Typography>;
      case 'list':
      case 'email-list':
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {String(masterDataValue)
              .split(',')
              .map((v: string, i: number) => (
                <Chip key={i} label={v.trim()} size="small" />
              ))}
          </Box>
        );
      case 'html':
      case 'css':
        return (
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              backgroundColor: '#f7f7f7',
              padding: 2,
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              maxHeight: 150,
              overflowY: 'auto',
            }}>
            {masterDataValue}
          </Box>
        );
      case 'string':
      default:
        return (
          <Tooltip title={masterDataValue}>
            <Typography
              noWrap
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {masterDataValue}
            </Typography>
          </Tooltip>
        );
    }
  };

  const getList = useCallback(
    async (filterData: PageProps) => {
      if (!id) return;
      const body = filterData || {
        page: index + 1,
        size: pageSize,
        filter: filter,
        sort: ['dataType:asc', 'masterDataKey:asc'],
      };
      try {
        setLoading(true);
        const { data } = await axiosInstance.post(`${URLS.orgMasterDataList(Number(id))}?isParent=${isParent}`, body);
        const content = data?.content?.map((item: any) => ({
          ...item,
          masterDataValue: renderValue(item),
        }));
        setPageResponse({ ...data, content });
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    },
    [id, isParent, index, pageSize, filter],
  );

  useEffect(() => {
    void getList(null as any);
  }, [getList]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
    setIsCustomKey(false);
    setDataType('String');
    setInputType('text');
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      if (!id) return;
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(`${URLS.orgMasterDataById(recordId)}?isParent=${isParent}`);
          const type = data?.dataType || 'String';
          setDataType(type);
          setInputType(getInputType(type));
          setFormValues({
            masterDataKey: data?.masterDataKeyLabel ?? data?.masterDataKey ?? '',
            masterDataValue: type === 'Boolean' ? data?.masterDataValue === 'true' : data?.masterDataValue ?? '',
          });
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        setIsCustomKey(false);
        setFormValues({
          masterDataKey: '',
          customMasterDataKey: '',
          dataType: '',
        });
        try {
          setDrawerLoading(true);
          await fetchDropdowns();
        } finally {
          setDrawerLoading(false);
        }
      }
      setDrawerOpen(true);
    },
    [id, isParent, fetchDropdowns],
  );

  const addFormModel: FormModel[] = useMemo(
    () => [
      {
        label: strings.organization_master_data_column_key,
        value: '',
        size: 'medium',
        autoFocus: true,
        type: 'drop-down',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'masterDataKey',
        validators: [{ check: Validators.validateMandatoryField }],
        responsive: { xs: 12 },
        required: true,
        options: [
          ...masterDataKeyOptions,
          { label: strings.organization_master_data_custom_key_option, value: '__custom__' },
        ],
      },
      {
        label: strings.organization_master_data_custom_key,
        value: '',
        size: 'medium',
        autoFocus: false,
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'customMasterDataKey',
        validators: [],
        responsive: { xs: 12 },
        required: false,
        disabled: !isCustomKey,
      },
      {
        label: strings.organization_master_data_column_data_type,
        value: '',
        size: 'medium',
        autoFocus: false,
        type: 'drop-down',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'dataType',
        validators: [{ check: Validators.validateMandatoryField }],
        responsive: { xs: 12 },
        required: true,
        options: dataTypeOptions,
      },
    ],
    [dataTypeOptions, masterDataKeyOptions, isCustomKey],
  );

  const editFormModel = useMemo(
    () => EditOrgMasterDataFormModel(strings, dataType, inputType),
    [dataType, inputType],
  );

  const handleSave = async (body: FormDataModel) => {
    if (!id) return;
    try {
      setSaving(true);
      if (editingId) {
        const value =
          dataType === 'Boolean' ? String(Boolean(body.masterDataValue)) : String(body.masterDataValue ?? '').trim();
        await axiosInstance.put(`${URLS.orgMasterDataById(editingId)}?isParent=${isParent}`, {
          masterDataValue: value,
        });
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_update_message,
        });
      } else {
        const resolvedKey =
          body.masterDataKey === '__custom__'
            ? String(body.customMasterDataKey || '').trim()
            : String(body.masterDataKey || '').trim();
        if (!resolvedKey) {
          setMessage({ display: true, severity: 'error', message: strings.requiredText });
          return;
        }
        await axiosInstance.post(`${URLS.orgMasterDataById(id)}?isParent=${isParent}`, {
          masterDataKey: resolvedKey,
          dataType: body.dataType,
        });
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
      }
      closeDrawer();
      await getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: '',
      searchField: 'search',
      searchPlaceholder: 'Search by key or value',
      AddButtonText: strings.add_organization_master_data,
      listApi: id ? `${URLS.orgMasterDataList(Number(id))}?isParent=${isParent}` : '',
      addPrivilege: isBbrAdmin ? privileges.edit_org_master_data : null,
      editPrivilege: privileges.edit_org_master_data,
      customAdd: () => openDrawer(null),
      customEdit: (recordId: number) => openDrawer(recordId),
      tableColumn: [
        {
          field: 'masterDataKey',
          headerName: strings.organization_master_data_column_key,
          flex: 1,
          sortable: true,
          renderCell: (_value: any, row: any) => row?.masterDataKeyLabel ?? row?.masterDataKey ?? '-',
        },
        {
          field: 'masterDataValue',
          headerName: strings.organization_master_data_column_value,
          flex: 1,
          sortable: true,
        },
      ],
    }),
    [id, isParent, isBbrAdmin, openDrawer],
  );

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <ListPage
        isWhiteSpace={false}
        isLoading={loading}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.edit_organization_master_data : strings.add_organization_master_data}
        isEdit={Boolean(editingId)}
        formModel={editingId ? editFormModel : addFormModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={false}
        onClose={closeDrawer}
        onSave={handleSave}
        onFieldChange={(field, value) => {
          if (!editingId && field === 'masterDataKey') {
            setIsCustomKey(value === '__custom__');
          }
        }}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default OrgMasterDataList;
